import React, { useState, useEffect, useRef } from "react";
import { IonButton, IonCard, IonCardContent, IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import { Helmet } from "react-helmet";

const objects = [
    { name: "Pencil", long: "pencil-long.png", short: "pencil-short.png" },
    { name: "Ruler", long: "ruler-long.png", short: "ruler-short.png" },
    { name: "Snake", long: "snake-long.png", short: "snake-short.png" },
    { name: "Ladder", long: "ladder-long.png", short: "ladder-short.png" },
    { name: "Bridge", long: "bridge-long.png", short: "bridge-short.png" },
];

const LongShort: React.FC = () => {
    const [question, setQuestion] = useState("");
    const [target, setTarget] = useState("long");
    const [selectedObject, setSelectedObject] = useState(objects[0]);
    const [message, setMessage] = useState("");
    const [showImage, setShowImage] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    useEffect(() => {
        generateQuestion();
        document.title = "Long and Short Worksheets for UKG | Pre-Math Concept Activities";
    }, []);

    const generateQuestion = () => {
        const randomObject = objects[Math.floor(Math.random() * objects.length)];
        const randomTarget = Math.random() > 0.5 ? "long" : "short";

        setSelectedObject(randomObject);
        setTarget(randomTarget);
        setQuestion(`Which one is ${randomTarget}?`);
        setMessage("");
    };

    const checkAnswer = (choice: string) => {
        if (choice === target) {
            setShowImage(true);
            setTimeout(() => {
                generateQuestion()
                setShowImage(false);
                setMessage("")
            }, 5000)
        } else {
            setMessage("❌ Try Again!");
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content="Teach UKG kids the concept of long and short using fun worksheets and real-life examples. Download printable length comparison activities to enhance observation skills." />
                <link rel="canonical" href="https://mamasguidance.com/ukg/mathematics/pre-math-concepts/long-short" />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className="contentPart matchImage match-container" style={{ textAlign: "center", padding: "20px" }}>
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>Long and Short</h1>

                                    <p>
                                        Teaching the difference between <strong>long</strong> and <strong>short</strong> is an important early math concept. It helps children develop a sense of measurement, comparison, and observation. Our <strong>UKG long and short worksheets</strong> are designed to introduce this concept in a simple and visual way.
                                    </p>

                                    <h2>📏 What Does Long and Short Mean?</h2>
                                    <p>
                                        “Long“ means something has more length, while “short“ has less length. Example:
                                        <ul>
                                            <li>A pencil that is 10 cm is <strong>longer</strong> than a pencil that is 5 cm.</li>
                                            <li>A rope that stretches across the room is <strong>long</strong>, and a shoelace is <strong>short</strong>.</li>
                                        </ul>
                                    </p>

                                    <h2>🎯 Learning Objectives</h2>
                                    <ul>
                                        <li>Understand and identify long and short objects</li>
                                        <li>Compare the lengths of two or more items</li>
                                        <li>Use correct vocabulary: <strong>long, short, longer, shorter</strong></li>
                                        <li>Build early measurement and sorting skills</li>
                                    </ul>

                                    <h2>🖍️ Activities in the Worksheets</h2>
                                    <ul>
                                        <li>Match long and short objects</li>
                                        <li>Tick the longer object from a pair</li>
                                        <li>Arrange items in order of length</li>
                                        <li>Color the shortest or longest item</li>
                                        <li>Use real-life pictures (<strong>like pencils, ribbons, ropes</strong>) for visual learning</li>
                                    </ul>

                                    <h2>👩‍🏫 Tips for Parents & Teachers</h2>
                                    <ul>
                                        <li>Use household objects (<strong>like spoons, straws, belts</strong>) to demonstrate length</li>
                                        <li>Let children arrange items from longest to shortest</li>
                                        <li>Encourage observation during walks (<strong>e.g., “Which stick is longer?“</strong>)</li>
                                        <li>Use storytelling with comparisons (<strong>e.g., “The giraffe’s neck is long”</strong>)</li>
                                    </ul>


                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/ukg/mathematics/pre-math-concepts/long-short"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Long - Short</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/mathematics/pre-math-concepts/heavy-light"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Heavy - Light</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/mathematics/pre-math-concepts/front-back"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Front - Back</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>🌟 Mama’s Guidance Tip</h2>
                                    <p>
                                        Don’t just use worksheets—use playtime! Comparing toothbrushes, spoons, and sticks can make learning natural and fun for your little one.
                                    </p>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>Long and Short</h1>

                                <p>
                                    Teaching the difference between <strong>long</strong> and <strong>short</strong> is an important early math concept. It helps children develop a sense of measurement, comparison, and observation. Our <strong>UKG long and short worksheets</strong> are designed to introduce this concept in a simple and visual way.
                                </p>

                                <h2>📏 What Does Long and Short Mean?</h2>
                                <p>
                                “Long“ means something has more length, while “short“ has less length. Example:
                                    <ul>
                                        <li>A pencil that is 10 cm is <strong>longer</strong> than a pencil that is 5 cm.</li>
                                        <li>A rope that stretches across the room is <strong>long</strong>, and a shoelace is <strong>short</strong>.</li>
                                    </ul>
                                </p>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Understand and identify long and short objects</li>
                                    <li>Compare the lengths of two or more items</li>
                                    <li>Use correct vocabulary: <strong>long, short, longer, shorter</strong></li>
                                    <li>Build early measurement and sorting skills</li>
                                </ul>

                                <h2>🖍️ Activities in the Worksheets</h2>
                                <ul>
                                    <li>Match long and short objects</li>
                                    <li>Tick the longer object from a pair</li>
                                    <li>Arrange items in order of length</li>
                                    <li>Color the shortest or longest item</li>
                                    <li>Use real-life pictures (<strong>like pencils, ribbons, ropes</strong>) for visual learning</li>
                                </ul>

                                <h2>👩‍🏫 Tips for Parents & Teachers</h2>
                                <ul>
                                    <li>Use household objects (<strong>like spoons, straws, belts</strong>) to demonstrate length</li>
                                    <li>Let children arrange items from longest to shortest</li>
                                    <li>Encourage observation during walks (<strong>e.g., “Which stick is longer?“</strong>)</li>
                                    <li>Use storytelling with comparisons (<strong>e.g., “The giraffe’s neck is long”</strong>)</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/ukg/mathematics/pre-math-concepts/long-short"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Long - Short</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/mathematics/pre-math-concepts/heavy-light"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Heavy - Light</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/mathematics/pre-math-concepts/front-back"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Front - Back</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🌟 Mama’s Guidance Tip</h2>
                                <p>
                                    Don’t just use worksheets—use playtime! Comparing toothbrushes, spoons, and sticks can make learning natural and fun for your little one.
                                </p>
                            </article>
                            <h2>{question}</h2>
                            <div className="mt-5 mb-5" style={{ display: "flex", justifyContent: "center", gap: "20px" }}>
                                <img src={process.env.REACT_APP_API_URL + selectedObject.long} alt="Long Object" style={{ width: "100px" }} onClick={() => checkAnswer("long")} />
                                <img src={process.env.REACT_APP_API_URL + selectedObject.short} alt="Short Object" style={{ width: "100px" }} onClick={() => checkAnswer("short")} />
                            </div>
                            <p>{message}</p>
                        </div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default LongShort;
